
// 1. Third-Party Libraries
import { createAsyncThunk } from "@reduxjs/toolkit";
import { format } from "date-fns";

// 2. State Management & Initial State
import { setFormWaringData, setFormWarningMessages, showModalWindow, showNotifications } from "./ClaimBetaSlice";

// 3. Services / APIs
import service from "../services/service";
import claim_service from '../services/claim_service';
import service1 from '../../../customerSetup/providerManagement/service';
import service3 from '../../../dictionaries/revenueCode/service';
import chargePanelService from "../../../dictionaries/ChargePanels/service";

// 4. Local Helpers, Mappers & Validations
import { buildAllChangedFieldsPayload, claimFormWaringMessage, createSaveData, fetchFeeRequestDataFormatter, handleApiErrors, handleIdentifierErrors, normalizeProcedureRow, processAmbulanceClaim, setWarningFromServer } from "./claimDataGenerator";
import { APIErrorHandler, claimValidation } from "./claimAlertValidations";
import { formatEscalateError, getValidationError, handleEscalateClaimErrors } from "./errorMappers";

// 5. Global Utilities & Configs
import { getStorage } from "../../../../utilities/browserStorage";
import { ACTIVE_TYPES, CLAIM_ON_HOLD, DEFAULT_PAGING_SIZE } from "../../../../utilities/staticConfigs";
import { ADD_SUCCESS, DELETE_SUCCESS, UPDATE_SUCCESS } from "../../../../utilities/labelConfigs";
import { CLIENT, SUPERVISOR } from "../../../../utilities/dictionaryConstants";
import i18n from "../../../../utilities/i18n";

/**
 * @description get last 10 table data 
 * @param query
 */
export const getSearchClaimsTableData = createAsyncThunk(
    'claimbeta/searchClaimsTableData',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await service.GetListPatientClaims(payload.query);
            if (response?.data?.error_message)
                return rejectWithValue({ error_message: response?.data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error") });
            return { data: response.data, isSearch: payload.search }
        } catch (error) {
            return rejectWithValue(i18n.t("claim_beta.api_errors.error_fetching_claim_list"));
        }
    }
)

/**
 * @description api is used to call all details of existing claim such as service line, billing info,additional info
 * @params payload  Either the claimPK (string|number) or an object { claimPK, claimStageId }
 */
export const getEditDetails = createAsyncThunk(
    'claimbeta/getEditDetails',
    async (payload, thunkAPI) => {
        try {
            const claimPK = (payload && typeof payload === 'object') ? payload.claimPK : payload;
            let claimInfo = await service.GetGeneralClaim(claimPK).then(response => {
                if (response.data) {
                    return response.data;
                }
                else {
                    if (response.error_data) {
                        throw new Error(response.error_data.error_message)
                    }
                }
            });
            let billingInfoPk = claimInfo?.pks?.billing_info_pk;
            let additionalClaimInfoPk = claimInfo?.pks?.additional_claim_info_pk;
            let serviceLinesPk = claimInfo?.pks?.diagnosises_pk

            let [billingInfo, procedureInfo, additionalInfo, responsibilityType] = await Promise.all([
                service.GetBillingInfoClaim(billingInfoPk).then(response => {
                    if (response.data) {
                        return response.data;
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                service.GetServiceLineClaim(serviceLinesPk).then(response => {
                    if (response.data) {
                        return response.data;
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                service.GetAdditionalClaim(additionalClaimInfoPk).then(response => {
                    if (response.data) {
                        return response.data;
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                service.GetResponsibilityType(claimInfo?.patient ? claimInfo?.patient : "").then(response => {
                    if (response.data) {
                        return response.data.responsibility_list
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
            ]);


            if (claimInfo?.authorization_referral) {
                let patient = claimInfo?.patient;
                let provider =
                    claimInfo?.authorization_referral === 1
                        ? billingInfo?.billing_provider_id
                        : billingInfo?.referring_provider_id;
                setTimeout(() => {
                    thunkAPI.dispatch(
                        fetchIdentifiers({
                            patient: patient,
                            auth: claimInfo?.authorization_referral,
                            provider: provider ? provider : "",
                            claimPK: claimInfo?.id
                        })
                    );
                }, 250);
            }
            return {
                claimInfo,
                billingInfo,
                procedureInfo,
                additionalInfo,
                responsibilityType,
                payload: claimPK
            };
        } catch (error) {
            /**
             * if any api get an error response it will close the claim tab automatically
             */
            const state = thunkAPI.getState();
            let { claimManagement } = state;
            const claimPKForLookup = (payload && typeof payload === 'object') ? payload.claimPK : payload;
            let openedCliamDetails = claimManagement?.claimParentTabs?.find((item) => item.pk == claimPKForLookup);
            if (openedCliamDetails) {
                console.warn("wrn")
            }
            return thunkAPI.rejectWithValue(i18n.t("claim_beta.api_errors.error_fetching_claim_info") + ': ' + error.message);
        }
    }
)



//getting patient details on patient pk of selected patients
export const getPatientDetails = createAsyncThunk(
    "claimbeta/patient-pk-details", async (payload, thunkAPI) => {
        try {
            const promises = [
                await service.GetPatientDetails(payload.patient_pk).then(response => {
                    if (response.data) {
                        return response.data
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                await service.GetPatientDXCPTDetails(payload.patient_pk).then(response => {
                    if (response.data) {
                        return response.data
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                await service.GetPatientAccidentInfo(payload.patient_pk).then(response => {
                    if (response.data) {
                        return response.data
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                await service.GetPracticeBasicData(getStorage('practice')).then(response => {
                    if (response.data) {
                        return response.data
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
                await service.ListAccidentTypes(DEFAULT_PAGING_SIZE, 0, payload.claim_pk ? payload.claim_pk : "").then(response => {
                    if (response.data) {
                        return response.data
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),

                /**
                 * getting responsibility type for the patient 
                */

                await service.GetResponsibilityType(payload.patient_pk ? payload.patient_pk : "").then(response => {
                    if (response.data) {
                        const responsibilityList = response?.data?.responsibility_list ?? [];
                        return responsibilityList
                    } else {
                        throw new Error(response?.error_data?.error_message ?? i18n.t("claim_beta.api_errors.unexpected_error"))
                    }
                }),
            ]

            const [
                billingData,
                serviceData,
                additionalData,
                practiceData,
                accidentList,
                responsibilityType,
            ] = await Promise.all(promises);

            return {
                billingData,
                serviceData,
                additionalData,
                practiceData,
                accidentList,
                responsibilityType,
            };

        } catch (error) {
            return thunkAPI.rejectWithValue(i18n.t("claim_beta.api_errors.error_fetching_claim_info") + ': ' + error.message)
        }
    }
)

/**
 * if there is no pos against the cpt it will take service location list cpt
 */
export const GetServiceLocationPOSList = createAsyncThunk(
    "claimbeta/servicelocationpos", async (payload) => {
        const result = await service.GetFacilityPos(payload);
        if (result) {
            return result.data
        }
    }
)

export const GetCPTCodeListWithDate = createAsyncThunk(
    "claimbeta/getcptcodelistwithdate", async (payload) => {
        let practicePK = getStorage('practice');
        let result;
        if (!payload.isAdvanceEnabled && payload.query) {
            result = await service.GetCPTCodeList(0, 0, practicePK, payload.type, payload.query, payload.claimPK, payload.dos);
        } else {
            result = { data: [] }
        }
        return { data: result.data, request: payload };
    }
)

export const fetchIdentifiers = createAsyncThunk(
    'claimbeta/fetchIdentifiers',
    async ({ patient, auth, provider, claimPK }, { rejectWithValue }) => {
        try {
            const response = await service.GetIdentifiers(patient, auth, provider, claimPK);
            if (response.data) {
                return { data: response.data, claimPK: claimPK };
            }
            if (response.error_data) {
                const { message } = handleIdentifierErrors(response.error_data);
                return rejectWithValue({ msg: message });
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);


export const fetchUnAppliedFee = createAsyncThunk(
    "claimbeta/fetch-unapplied_fee", async (payload, { getState, rejectWithValue }) => {
        let { type, id, claimPK } = payload;
        const state = getState();
        let { claimManagement } = state
        let { billingInfoDetails, serviceLineDetails, additionalClaimDetails } = claimManagement;
        const requestData = await fetchFeeRequestDataFormatter(billingInfoDetails, serviceLineDetails, additionalClaimDetails, type, id)
        try {
            const result = await claim_service.FetchFeeDetails(requestData);
            if (result.data && result.status == 200) {
                return { data: result.data, payload, claimPK: claimPK, type: type };
            } else (result.status == 400)
            {
                return rejectWithValue(i18n.t("claim_beta.api_errors.unexpected_error"));
            }
        } catch (error) {
            return rejectWithValue(error)
        }
    }
)

export const ActivateInactiveClaim = createAsyncThunk(
    "claimbeta/ActivateInactiveClaim", async (payload, { dispatch, rejectWithValue }) => {
        let data = { 'active': true }
        const result = await service.UpdateClaimActive(payload, data);
        if (result.data) {
            dispatch(showModalWindow({ key: "activeClaimModal", value: false, claimPK: payload }))
            return {
                status: "success",
                claimPK: payload
            }
        } else {
            return rejectWithValue(i18n.t("claim_beta.api_errors.error_activating_claim"));
        }
    }
)


export const getPatientHistory = createAsyncThunk(
    "claimbeta/patientHistory", async (payload) => {
        const result = await service.GetPatientHistory(payload);
        return result.data;
    }
)

// api for add diagnosis code using paste
export const getDiagnosisCodeOnPaste = createAsyncThunk(
    "claimbeta/getDiagnosisCodeOnPaste", async (payload) => {
        let { value, type, claimPK } = payload;
        const result = await service.GetICD10List(0, 0, getStorage('practice'), type, value, claimPK);
        return { result: result.data, request: payload };
    }
)


/**
 * GET LIST OF CHARGE PANELS FOR THE CURRENT PRACTICE
 * @async
 */
export const getChargePanelsList = createAsyncThunk(
    "claimbeta/getChargePanelsList",
    async (_, { rejectWithValue }) => {
        try {
            const result = await chargePanelService.ListChargePanels({
                practice_pk: getStorage('practice'),
                page: 0,
                page_size: null,
                status: ACTIVE_TYPES.find(({ name }) => name === "Active")?.id,
            });

            if (Array.isArray(result.data)) {
                return result;
            } else {
                return rejectWithValue(i18n.t("claim_beta.api_errors.error_fetching_panels_list"));
            }
        } catch (error) {
            return rejectWithValue(error.message || i18n.t("claim_beta.api_errors.error_fetching_charge_panels"));
        }
    }
);



/**
 * GET PROCEDURES OF A CHARGE PANEL
 * @param {Number} chargePanelId
 * @async
 */
export const addNewProcedureFromChargePanel = createAsyncThunk(
    "claimbeta/addNewProcedureFromChargePanel",
    async (chargePanelId, { rejectWithValue }) => {
        try {
            const response = await chargePanelService.GetPanelProceduresList(chargePanelId);

            if (response.status != 200 || !Array.isArray(response.data)) {
                return rejectWithValue(i18n.t("claim_beta.api_errors.error_fetching_panel_procedures"));
            } else {
                return response
            }
        } catch (error) {
            return rejectWithValue(error.message || i18n.t("claim_beta.api_errors.error_fetching_panel_procedures"));
        }
    }
);


/**
 * API Call functionality to fetch ar-claim table data against a given query
 */
export const getARClaimsData = createAsyncThunk(
    "claimbeta/getARClaimsData",
    async (payload, thunkAPI) => {
        try {
            const res = await claim_service.GetListARPatientClaims(payload);
            if (res.data) {
                return { data: res.data };
            } else {
                return thunkAPI.rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_ar_claim_data") })
            }
        } catch (error) {
            return thunkAPI.rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_ar_claim_data") })
        }
    }
);

/*
 * Function getDropDownListData fetches all the drop-down-data required for the page in one shot using promise chaining 
 */
export const getDropDownListData = createAsyncThunk(
    "claimbeta/getDropDownListData",
    async (payload, thunkAPI) => {
        const pageSize = 0;
        const page = 0;
        const practicePK = getStorage('practice');
        const type = 'dropdown';
        /**
         * The promises array contains the promises returned by each API call, 
         * which can be resolved using Promise.all(promises) to wait for all the requests to complete.
         */
        const promises = [
            claim_service.ListClaimStatus(pageSize, page, practicePK, type).then(response => response.data),
            service1.getProvidersData(pageSize, page, practicePK, true).then(response => response.data),
            service.ServiceLocationLists(practicePK, null, null, true).then(response => {
                let locations = []
                response.data.map((item) => {
                    item.id = item.Id + '||' + item.location_type
                    locations.push(item)
                })
                return locations
            }),
            claim_service.ARStatusNoteList(0, 0, practicePK).then(response => {
                let data = []
                response.data.map((item) => {
                    data.push({
                        id: item.id,
                        name: item.name
                    })
                })
                return data
            }),
            claim_service.ARActionNoteList(0, 0, practicePK).then(response => {
                let data = []
                response.data.map((item) => {
                    data.push({
                        id: item.id,
                        name: item.name
                    })
                })
                return data
            }),
            service3.GetListRevenueCode(pageSize, page).then(response => response.data),
            claim_service.ListInsuranceNames(pageSize, 0, practicePK).then(response => response.data),
            claim_service.ListCPTCodes(practicePK).then(response => response.data)
        ];
        try {
            const [
                claimStatusList,
                providerList,
                serviceLocationList,
                arStatusList,
                arActionCodeList,
                revenueCodeList,
                payerList,
                cptCodesList
            ] = await Promise.all(promises);

            return {
                claimStatusList,
                providerList,
                serviceLocationList,
                arStatusList,
                arActionCodeList,
                revenueCodeList,
                payerList,
                cptCodesList
            };
        } catch (error) {
            return thunkAPI.rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_dropdown_data") })
        }
    }
);


/**
 * API Call functionality to fetch ar-claim header data 
 */
export const getARClaimsTabData = createAsyncThunk(
    "claimbeta/getARClaimsHeaderData",
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            const res = await claim_service.GetOpendARClaims(payload);
            if (res.data) {
                dispatch(GetARClaimsList(payload));
                return { data: res.data };
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_ar_header_data") })
        }
    }
);

/**
 * get patient ledger details
 */
export const getPatientLedgerList = createAsyncThunk(
    "claimbeta/getPatientLedgerList",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await claim_service.GetPatientLederList(payload);
            if (res.data) {
                return { data: res.data };
            } else {
                return rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_patient_ledger") })
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_patient_ledger") })
        }
    }
);


export const fetchAssignClaimList = createAsyncThunk(
    "claimbeta/fetchAssignClaimList",
    async (practicePK, { rejectWithValue }) => {
        try {
            const response = await claim_service.ListARClaimPraciceUsers(practicePK);
            return response.data;
        } catch (error) {
            console.error("Failed to fetch Assign Claim List", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


/**
 * API Call functionality to fetch ar-claim header data 
 */
export const getARClaimNotes = createAsyncThunk(
    "claimbeta/getARClaimNotes",
    async (payload, thunkAPI) => {
        try {
            const response = await claim_service.ListArClaimNotes(payload);
            return response.data.results
        } catch (error) {
            return thunkAPI.rejectWithValue({ errorMessage: i18n.t("claim_beta.api_errors.error_fetching_ar_header_data") })
        }
    }
);

/**
 * API Call functionality to fetch ar-claim note details
 */
export const AssignARClaimList = createAsyncThunk(
    "claimbeta/AssignARClaimList",
    async (payload, { rejectWithValue }) => {
        const practicePK = getStorage('practice');
        try {
            const result = await claim_service.ListARClaimPraciceUsers(practicePK);
            return result.data
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.error_fetching_ar_assign_list") })
        }
    }
);


/**
 * API Call functionality to fetch ar-claim note details
 */
export const AssignToUserAction = createAsyncThunk(
    "claimbeta/AssignToUserAction",
    async (payload, { rejectWithValue }) => {
        try {
            let message
            const response = await claim_service.AssignUserAgainstPractice(payload);
            if (response.status === 201 || response.status === 200) {
                if (response.status === 201)
                    message = ADD_SUCCESS;
                else message = UPDATE_SUCCESS;
            }

            return message;
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);


/**
 * API Call functionality to fetch ar-claim note details
 */
export const InActiveClaim = createAsyncThunk(
    "claimbeta/InActiveClaim",
    async (payload, { rejectWithValue }) => {
        try {
            let response = await claim_service.ClaimInactive(payload, { 'active': false });
            if (response.status === 200) {
                return { message: DELETE_SUCCESS }
            } else {
                return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
            }
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);


/**
 * API Call functionality to fetch ar-claim note details
 */
export const UnAssignClaims = createAsyncThunk(
    "claimbeta/UnAsssignClaims",
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            let response = await claim_service.UnAssignUserAgainstPractice(payload);
            if (response.status === 204) {
                dispatch(AssignARClaimList())
                return { message: i18n.t("claim_beta.api_errors.unassigned_user") }
            } else {
                return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
            }
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);

/**
 * APi to get ar claims
 */
export const GetARClaimsList = createAsyncThunk(
    "claimbeta/GetARClaimsList",
    async (payload, { rejectWithValue }) => {
        try {
            let response = await claim_service.GetARClaimsListData(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);


/**
 * APi to get ar claims
 */
export const getCptCodesForClaim = createAsyncThunk(
    "claimbeta/getCptCodesForClaim",
    async (payload, { rejectWithValue }) => {
        let { claim_id, index } = payload
        try {
            let response = await claim_service.GetARClaimProcedureList(claim_id);
            return { data: response.data, index: index, id: claim_id };
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);

/**
 * APi to get ar claims
 */
export const getActivityOfProcedure = createAsyncThunk(
    "claimbeta/getActivityOfProcedure",
    async (payload, { rejectWithValue }) => {
        let { id, index, main_index } = payload
        try {
            let response = await claim_service.GetARClaimActivityOfProcedure(id);
            return { data: response.data, index: index, id: id, main_index: main_index };
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.something_went_wrong") })
        }
    }
);

/**
 * API to complete a claim
 */
export const completeClaimAPI = createAsyncThunk(
    "claimbeta/completeClaim",
    async ({claimStageIds, responseText}, { rejectWithValue }) => {
        const errorMessages = {
            "all_claim_stage_ids_must_belong_to_same_claim": i18n.t("claim_beta.api_errors.all_claim_stage_ids_must_belong_to_same_claim"),
            "invalid_claim_stage": i18n.t("claim_beta.api_errors.invalid_claim_stage"),
            "claim_stage_id_empty": i18n.t("claim_beta.api_errors.claim_stage_id_empty"),
            "can_not_update_status_to_complete_claim_status_either_in_todo_or_completed": i18n.t("claim_beta.api_errors.can_not_update_status_to_complete_claim_status_either_in_todo_or_completed"),
            "approve_or_reprove_all_stages_before_completing_the_claim": i18n.t("claim_beta.api_errors.approve_or_reprove_all_stages_before_completing_the_claim")
        };
        try {
            const ids = Array.isArray(claimStageIds) ? claimStageIds : [claimStageIds];
            if (ids.length === 0) {
                return rejectWithValue({ message: i18n.t("claim_beta.api_errors.claim_stage_ids_required") });
            }

            const response = await service.ClaimCompleteAPI(ids, responseText);
            if (response.status === 200 || response.status === 201) {
                return response.data;
            }
            if (response.status === 400) {
                const apiMessage = response.data?.message;
                const translatedMessage =
                    errorMessages[apiMessage] || i18n.t("claim_beta.api_errors.complete_claim_failed");
                return rejectWithValue({ message: translatedMessage });
            }
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.complete_claim_error") });
        } catch (error) {
            const apiMessage = error.response?.data?.message;
            const translatedMessage = errorMessages[apiMessage] || i18n.t("claim_beta.api_errors.something_went_wrong");
            return rejectWithValue({ message: translatedMessage });
        }
    }
);

/**
 * Get claim workflow data for View Activity
 */
export const getClaimWorkflow = createAsyncThunk(
    "claimbeta/getClaimWorkflow",
    async (claimPK, { rejectWithValue }) => {
        try {
            const response = await claim_service.GetClaimWorkflow(claimPK);
            if (response.data) {
                return { data: response.data };
            } else {
                if (response.status === 404) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_not_found') });
                } else if (response.status === 400) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_id_is_required') });
                }
                return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_logs') });
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_workflow') });
        }
    }
);

/**
 * Get claim activity logs for View Activity
 */
export const getClaimActivityLogs = createAsyncThunk(
    "claimbeta/getClaimActivityLogs",
    async (claimPK, { rejectWithValue }) => {
        try {
            const response = await claim_service.GetClaimActivityLogs(claimPK);
            if (response.data) {
                return { data: response.data };
            } else {
                if (response.status === 404) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_not_found') });
                } else if (response.status === 400) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_id_is_required') });
                }
                return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_logs') });
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_logs') });
        }
    }
);

/**
 * Single-button-click update thunk.
 *
 * Behaviour:
 *  - With no payload (or `payload.keys` omitted): diffs ALL claim sections
 *    (professional / billing / service line / additional) against `duplicateDataSet`
 *    and returns a combined payload of only the changed fields per section.
 *  - With `payload` as a string key or `payload.keys` as an array of keys:
 *    diffs only those sections.
 *
 * Return value:
 *   {
 *     hasChanges: boolean,
 *     changedSections: string[],          // section keys that actually changed
 *     changedData: { [sectionKey]: {...changed fields...} }
 *   }
 */
export const updateClaimAPI = createAsyncThunk(
    "claimbeta/updateClaim", async (payload, { getState, rejectWithValue, dispatch }) => {
    try {
        // Safe destructuring with fallback to prevent crashes if payload is missing items
        let { claimPK, isClose = false, customClaimId } = payload || {};
        const state = getState();
        const { claimbeta, claimSelectInputManager } = state || {};
        const { professionalClaimDetails } = claimbeta || {};

        // const enable_advance_fee_schedule = getStorage('enable_advance_fee_schedule');
        // let { all, single } = claimbeta?.fee_field_status || {};
        let { isError, errorMessage, status } = claimValidation(claimbeta, claimSelectInputManager) || {};

        // const shouldShowFeeConfirmation = all || single;

        if (claimbeta?.additionalClaim?.transport_code == 2 && !claimbeta.additionalClaim?.return_trip_reason) {
            return { data : {}, isClose: false };
        }

        // if (shouldShowFeeConfirmation && enable_advance_fee_schedule == "true") {
        //     return rejectWithValue({ type: "fee_confirmation_modal", value: true })
        // }

        if (isError) {
            return rejectWithValue({ isopen: isError, message: errorMessage, status: status })
        }

        const professionalClaimData = buildAllChangedFieldsPayload(claimbeta, "professionalClaimDetails");
        const billingInfo = buildAllChangedFieldsPayload(claimbeta, "billingInfoDetails");
        const serviceLine = buildAllChangedFieldsPayload(claimbeta, "serviceLineDetails");
        const additionalClaim = buildAllChangedFieldsPayload(claimbeta, "additionalClaimDetails");

        const processedAdditionalClaim = processAmbulanceClaim(
            additionalClaim?.additionalClaimDetails,
            claimbeta?.additionalClaimDetails
        );
        
        const isClaimEmpty = Object.keys(professionalClaimData?.professionalClaimDetails || {}).length === 0;
        const isBillingEmpty = Object.keys(billingInfo?.billingInfoDetails || {}).length === 0;
        const isServiceEmpty = Object.keys(serviceLine?.serviceLineDetails || {}).length === 0;
        const isAdditionalEmpty = Object.keys(additionalClaim?.additionalClaimDetails || {}).length === 0;

        if (isClaimEmpty && isBillingEmpty && isServiceEmpty && isAdditionalEmpty) {
            return rejectWithValue({ isopen: true, message: i18n.t("claim_beta.api_errors.no_data_to_save"), status: "error",isClose:isClose })
        }

        let {
            professionaldata,
            billingData,
            diagnosisdata,
            procedureArray,
            additionaldata,
        } = createSaveData(
            professionalClaimData?.professionalClaimDetails,
            billingInfo?.billingInfoDetails,
            serviceLine?.serviceLineDetails,
            processedAdditionalClaim,
            professionalClaimDetails?.pks,
        ) || {};

        const data = {
            ...(!isClaimEmpty && professionaldata),
            ...(!isBillingEmpty && { billing_info: billingData }),
            ...(Object.keys(diagnosisdata || {}).length > 0 && { diagnosises: diagnosisdata }),
            ...(procedureArray?.length > 0 && { procedures: procedureArray }),
            ...(!isAdditionalEmpty && { additional_claim_info: additionaldata }),
        };

        let result = await service.ClaimUpateAPI(claimPK, data);

        if (result && (result.status === 201 || result.status === 200)) {
            if (result.status === 201) {
                dispatch(showNotifications({ isopen: true, message: ADD_SUCCESS, status: "success" }))
            }
            else {
                dispatch(showNotifications({ isopen: true, message: UPDATE_SUCCESS, status: "success" }))
                const isActiveExistsAndFalse = 'active' in professionaldata && professionaldata.active === false;
                if (!isClaimEmpty &&  isActiveExistsAndFalse) {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    dispatch(showModalWindow({ key: "inactiveClaimModal", value: false, claimPK: "",close_window:true}));
                    return { data: result.data, isClose: false,claimPK}; // Ensure a valid object is returned here
                }
            }

            let warningText = claimFormWaringMessage(result.data);
            let serverWarings = setWarningFromServer(result.data);
            if (serverWarings) {
                dispatch(setFormWaringData(serverWarings));
            }
            
            if (warningText) {
                dispatch(setFormWarningMessages(warningText));
                if (isClose) {
                    dispatch(showModalWindow({ key: "saveCloseConfirmation", value: true }));
                    return { data: result.data, isClose: false,claimPK}
                }
                else {
                    dispatch(showModalWindow({ key: "saveConfirmation", value: true }));
                    dispatch(getEditDetails(customClaimId));
                    if (claimPK) {
                        return { data: result.data, isClose: false,claimPK}
                    }
                }
            } else if (isClose) {
                return { data: result.data, isClose: true,claimPK}
            } else {
                if (claimPK) {
                    return { data: result.data, isClose: false,claimPK}
                }
            }
            return { data: result.data, isClose: false,claimPK}; // Fallback return for success path
        } else {
            let { errorMessage, inActiveModal } = APIErrorHandler(result) || {};
            dispatch(showModalWindow({ key: "inactiveClaimModal", value: inActiveModal }));
            return rejectWithValue({ isopen: true, message: errorMessage, statusTag: "error" })
        }
    }
    catch (error) {
        return rejectWithValue({ isopen: true, message: i18n.t("claim_beta.api_errors.something_went_wrong"), statusTag: "error" })
    }
});


/* Fetches the list of payments associated with a specific claim.
* Get claim activity logs for View Activity
*/
export const getEscalationComments = createAsyncThunk(
    "claimbeta/getEscalationComments",
    async (claimPK, { rejectWithValue }) => {
        try {
            const response = await claim_service.GetEscalationComments(claimPK);
            if (response.data) {
                return { data: response.data.results ?? [] };
            } else {
                if (response.status === 404) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_not_found') });
                } else if (response.status === 400) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_id_is_required') });
                }
                return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_comments') });
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_comments') });
        }
    }
);

export const getAuditorComments = createAsyncThunk(
    "claimbeta/getAuditorComments",
    async (claimPK, { rejectWithValue }) => {
        try {
            let practice=getStorage('practice')
            const response = await claim_service.GetAudtiorComments(claimPK,practice);
            if (response.data) {
                return { data: response.data.comments ?? [] };
            } else {
                if (response.status === 404) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_not_found') });
                } else if (response.status === 400) {
                    return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.claim_id_is_required') });
                }
                return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_comments') });
            }
        } catch (error) {
            return rejectWithValue({ errorMessage: i18n.t('claim_beta.api_errors.error_comments') });
        }
    }
);

export const fetchPracticeUsers = createAsyncThunk(
    "claimbeta/fetchPracticeUsers",
    async (_, { rejectWithValue }) => {
        try {
            const practicePK = getStorage('practice');
            const response = await claim_service.GetPracticeUsers(practicePK);
            if (response.data) {
                return response.data;
            }
            return rejectWithValue(i18n.t("claim_beta.api_errors.unexpected_error"));
        } catch (error) {
            return rejectWithValue(error.message || i18n.t("claim_beta.api_errors.something_went_wrong"));
        }
    })
/**
 * Fetches the list of payments associated with a specific claim.
 * * @param {string|number} claimId - The unique identifier of the professional claim.
 * @param {Object} thunkAPI - Standard Redux Toolkit thunk arguments (including rejectWithValue).
 * @returns {Promise<Array>} Resolves to the payment data array on success.
 * @throws {string|number} Rejects with the status code or error message on failure.
 */
export const fetchClaimPayments = createAsyncThunk(
    'claimPayments/fetchAll',
    async (claimId, { rejectWithValue }) => {
        try {
            const response = await service.GetClaimPayments(claimId);
            const statusCode = response?.error_data?.status_code || response?.status

            if (statusCode === 404 && response?.error_data?.message == "claim_not_found") {
                return rejectWithValue(i18n.t("claim_beta.api_errors.claim_not_found"));
            }

            if (response.status === 200) {
                return response.data ?? [];
            }
            return rejectWithValue(response?.error_data?.message || i18n.t("errorMessages.commonError"));
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchSupervisorEscalationDetails = createAsyncThunk(
    "claimbeta/fetchSupervisorEscalationDetails",
    async (claimStageIds, { rejectWithValue }) => {
        try {
            const practicePK = getStorage('practice');
            const response = await claim_service.GetSupervisorEscalationDetails(claimStageIds, practicePK);
            if (response.data && (response.status === 200 || response.status === 201)) {
                return response.data;
            } else {
                const message = response?.message;
                if (message) {
                    // Check if we have a direct mapping in i18n
                    const i18nKey = `claim_beta.api_errors.${message}`;
                    const translated = i18n.t(i18nKey);
                    if (translated && !translated.includes("missing")) {
                        return rejectWithValue(translated);
                    }
                    // Handle dynamic messages like "no_escalations_found_for_claim_stages: [172]."
                    if (message.startsWith('no_escalations_found_for_claim_stages')) {
                        return rejectWithValue(i18n.t('claim_beta.api_errors.no_escalations_found_for_claim_stages'));
                    }
                    return rejectWithValue(message);
                }
                return rejectWithValue(i18n.t("claim_beta.api_errors.something_went_wrong"));
            }
        } catch (error) {
            return rejectWithValue(error.message || i18n.t("claim_beta.api_errors.something_went_wrong"));
        }
    }
);

export const deEscalateClaimAPI = createAsyncThunk(
    "claimbeta/deEscalateClaimAPI",
    async (payload, { rejectWithValue, getState }) => {
        try {

            const state = getState();

            const { claimbeta } = state;

            let { deEscalationDetails } = claimbeta

            if (!payload) return rejectWithValue(i18n.t("claim_beta.api_errors.something_went_wrong"));

            if (!deEscalationDetails?.remarks || deEscalationDetails?.remarks.trim() === '') {
                return rejectWithValue(i18n.t("claim_beta.api_errors.remark_mandatory"));
            }

            const request = {
                manager_support_request_id: payload,
                response: deEscalationDetails?.remarks,
            };
            const response = await claim_service.DeEscalateClaim(request);
            if (response.data && (response.status === 200 || response.status === 201)) {
                return response.data;
            } else {
                const message = response.data?.message;
                if (message) {
                    const i18nKey = `claim_beta.api_errors.${message}`;
                    const translated = i18n.t(i18nKey);
                    // If translation fails, i18n-js typically returns the key or a missing message
                    if (translated && !translated.includes("missing")) {
                        return rejectWithValue(translated);
                    }
                    return rejectWithValue(message);
                }
                return rejectWithValue(i18n.t("claim_beta.api_errors.something_went_wrong"));
            }
        } catch (error) {
            return rejectWithValue(error.message || i18n.t("claim_beta.api_errors.something_went_wrong"));
        }
    }
);
/**
 * Fetches expanded details for a specific payment row (Lazy Loading).
 * * This thunk is triggered when a user expands a payment row. It normalizes 
 * varying API response shapes and maps procedure codes to a standard format.
 * * @param {Object} params - The request parameters.
 * @param {string|number} params.claimId - ID of the parent professional claim.
 * @param {string} params.paymentType - The type of payment (e.g., 'ERA', 'Patient').
 * @param {string|number} params.paymentId - The specific payment record ID.
 * @returns {Promise<Object>} Object containing the paymentId and normalized details.
 */
export const fetchPaymentDetail = createAsyncThunk(
    'claimPayments/fetchDetail',
    async ({ claimId, paymentType, paymentId }, { rejectWithValue }) => {
        try {
            //Calling the service
            const response = await service.GetClaimPaymentDetails(claimId, paymentType, paymentId);
            const errorMsgs = {
                "payment_not_found": i18n.t("claim_beta.api_errors.payment_not_found"),
                "Invalid_payment_type": i18n.t("claim_beta.api_errors.invalid_payment_type")
            }

            const statusCode = response?.error_data?.status_code || response?.status;
            const errorKey = response?.error_data?.message;

            if (statusCode === 404 || statusCode === 400) {
                // Return the translated message, or a fallback if the key doesn't match
                return rejectWithValue(errorMsgs[errorKey] || errorKey || i18n.t("errorMessages.commonError"));
            }
            //Standard success path
            const data = response.data;
            const payload = data?.results ?? data ?? {};
            const proceduresPayload = payload?.procedures ?? payload?.procedure_details ?? [];

            return {
                paymentId,
                paymentType,
                details: {
                    procedures: Array.isArray(proceduresPayload) ? proceduresPayload.map(normalizeProcedureRow) : [],
                    detailsLoaded: true
                }
            };

        } catch (error) {
            // This only runs if the service actually 'throws'
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

export const fetchSupportReasons = createAsyncThunk(
    "support/fetchReasons",
    async (_, { rejectWithValue }) => {
        try {
            const res = await service.GetSupportReasonDropdown();
            const raw = res?.data?.data ?? res?.data ?? [];
            return Array.isArray(raw) ? raw : [];
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);


// Async Thunk to handle submission
export const submitSupportRequest = createAsyncThunk(
    "support/submitRequest",
    async (_, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            let { claimbeta } = state;
            let { RequestDialogDetails, activeTab, professionalClaimDetails } = claimbeta;
            const currentPracticeId = getStorage("practice");
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");
            const { initiate_client_escalation } = userData
            const practiceId = Number(currentPracticeId);

            const currentDetails = RequestDialogDetails?.[activeTab] || {};
            let selectedStageIds = currentDetails.stageIds;
            const reasonId = currentDetails.reasonId;
            const description = String(currentDetails.explain ?? "").trim();

            const validationError = getValidationError({
                currentPracticeId,
                professionalClaimDetails,
                practiceId,
                selectedStageIds,
                reasonId,
                description,
                i18n
            });

            if (validationError) {
                return rejectWithValue(validationError);
            }

            const escalation_type = initiate_client_escalation && activeTab === CLIENT 
                    ? CLIENT
                    : SUPERVISOR;

            // if not selectedStageIds use state data
            if (!selectedStageIds || selectedStageIds.length === 0) {
                selectedStageIds = professionalClaimDetails?.claim_stage?.map(item => item.claim_stage_id);
            }
            const payload = {
                claim_stage_ids: selectedStageIds,
                escalation_type,
                reason_id: reasonId,
                description,
            };

            const res = await service.EscalateClaim(payload);
            if (res?.status === 200 || res?.status === 201) {
                return res;
            } else {
                const { message } = handleEscalateClaimErrors(res?.data || {});
                const msg =
                    message ||
                    formatEscalateError(res?.data) ||
                    res?.statusText ||
                    i18n.t("claim_beta.api_errors.unexpected_error") ||
                    "Request failed";
                return rejectWithValue(msg)
            }
        } catch (err) {
            return rejectWithValue(i18n.t("claim_beta.api_errors.unexpected_error") ||
                "Request failed");
        }
    }
);

export const escalateToManager = createAsyncThunk(
    "support/escalateToManager",
    async (passedStageIds,{ rejectWithValue, getState }) => {
        try {
            const state = getState();
            let { claimbeta } = state;
            let { RequestDialogDetails, professionalClaimDetails, deEscalationDetails } = claimbeta;
            const currentPracticeId = getStorage("practice");
            const practiceId = Number(currentPracticeId);
            let selectedStageIds = passedStageIds;

            const reasonId = RequestDialogDetails?.reasonId;
            const description = deEscalationDetails?.remarks;

            const validationError = getValidationError({
                currentPracticeId,
                professionalClaimDetails,
                practiceId,
                selectedStageIds,
                reasonId,
                description,
                i18n
            });

            if (validationError) {
                return rejectWithValue(validationError);
            }

            const payload = {
                claim_stage_ids: selectedStageIds,
                escalation_type: SUPERVISOR,
                reason_id: reasonId,
                description,
            };

            const res = await service.EscalateClaim(payload);
            if (res?.status === 200 || res?.status === 201) {
                return res;
            } else {
                const { message } = handleEscalateClaimErrors(res?.data || {});
                const msg =
                    message ||
                    formatEscalateError(res?.data) ||
                    res?.statusText ||
                    i18n.t("claim_beta.api_errors.unexpected_error") ||
                    "Request failed";
                return rejectWithValue(msg)
            }
        } catch (err) {
            return rejectWithValue(i18n.t("claim_beta.api_errors.unexpected_error") ||
                "Request failed");
        }
    }
);

export const deleteProcedureItem = createAsyncThunk(
    'claims/deleteProcedure',
    async (payload, { rejectWithValue }) => {
        try {
            let { procedureId, index, claimPK, payment_done } = payload;
            if (payment_done) {
                return rejectWithValue({ procedureId, index, payment_done: true, message: "" });
            }

            if (typeof procedureId === 'string' && procedureId.startsWith('New')) {
                return { claimPK, procedureId, index, payment_done };
            }

            const response = await service.ProcedureDeleteAPI(procedureId);
            if (response.status === 204) {
                // Return payload to the reducer
                return { claimPK, procedureId, index, payment_done };
            } else {
                return rejectWithValue({ procedureId, index, payment_done: false, message: i18n.t("errorMessages.commonError") });
            }
        } catch (error) {
            // Extracts the error string message instead of passing the entire object
            const errorMessage = error.response?.data?.message || error.message || i18n.t("errorMessages.commonError");
            return rejectWithValue({ procedureId: "", index: "", payment_done: false, message: errorMessage });
        }
    });

export const ClaimStatusChange = createAsyncThunk(
    "searchClaim/ClaimStatusChangeSearchClaim",
    async (payload, { rejectWithValue }) => {
        let { id, custom_claim_id } = payload;
        try {
            const errorMessage = {
                "invalid_claim_stage": i18n.t("my_work.api_error.invalid_claim_stage"),
                "required_fields_missing": i18n.t("my_work.api_error.required_fields_missing"),
                "invalid_user": i18n.t("my_work.api_error.invalid_user"),
                "can_not_update_status_to_complete_claim_status_either_in_todo_or_completed": i18n.t("my_work.api_error.can_not_update_status_to_complete_claim_status_either_in_todo_or_completed"),
                "claim_already_assigned_to_another_user": i18n.t("my_work.api_error.claim_already_assigned_to_another_user"),
            };
            let req = {
                claim_id: id,
            }
            const response = await service.claimStatusChange(req);
            if (response.status === 200) {
                return { data: response.data, id: custom_claim_id };
            }
            if (response.status === 400) {
                return rejectWithValue({ message: errorMessage[response.data.message] || i18n.t("errorMessages.commonError") });
            }
            return rejectWithValue({ message: i18n.t("errorMessages.commonError") });
        } catch (error) {
            return rejectWithValue(error || i18n.t("errorMessages.commonError"));
        }
    }
);

/**
 * API Call functionality to save ar claim note  
 */
export const ARClaimNotes = createAsyncThunk(
    "claimbeta/ARClaimNotes",
    async (payload, { rejectWithValue, getState }) => {
        try {
            // 1. Destructure payload from the component
            const { claimPK, notes } = payload; 
            
            const { claimbeta } = getState();
            const { arNotes, original_ar_notes } = claimbeta;

            // 2. Validations (Checked inside Redux)
            if (!claimPK) return rejectWithValue(i18n.t("errorMessages.claimIdMissing") || "Claim ID is missing!");
            if (!notes) return rejectWithValue(i18n.t("errorMessages.noteRequired") || "Notes are required!");
            if (!arNotes?.ar_status_code || !arNotes?.ar_action_code) return rejectWithValue(i18n.t("errorMessages.mandatoryFieldsRequired") || "Mandatory fields required!");
            if (!arNotes?.next_followup_date || arNotes.next_followup_date === 'Invalid Date') return rejectWithValue(i18n.t("errorMessages.invalidFollowUpDate") || "Invalid Follow-up Date!");

            let result, message;
            const formattedDate = format(new Date(arNotes.next_followup_date), "yyyy-MM-dd");

            if (!arNotes.id) {
                // --- ADD CASE ---
                const formData = {
                    notes: notes, // Uses trimmed notes from component
                    next_follow_up_date: formattedDate,
                    claim: claimPK, 
                    category: 'ar_claim',
                    ar_status: arNotes.ar_status_code,
                    ar_action: arNotes.ar_action_code,
                    ar_note_template: arNotes.selected_template_id
                };

                result = await service.AddArClaimNotes(formData);
                message = ADD_SUCCESS;
            } else {
                // --- EDIT CASE ---
                const editFormData = {};
                const orig = original_ar_notes || {}; 

                // Compare current state against original state
                if (notes !== orig.notes) editFormData.notes = notes;
                if (formattedDate !== orig.next_followup_date) editFormData.next_follow_up_date = formattedDate;
                if (arNotes.ar_status_code !== orig.ar_status_code) editFormData.ar_status = arNotes.ar_status_code;
                if (arNotes.ar_action_code !== orig.ar_action_code) editFormData.ar_action = arNotes.ar_action_code;
                if (arNotes.selected_template_id !== orig.selected_template_id) editFormData.ar_note_template = arNotes.selected_template_id;

                // Optimization: If nothing changed, skip the API call entirely
                if (Object.keys(editFormData).length === 0) {
                    return { result: orig, message: UPDATE_SUCCESS };
                }

                result = await service.UpdateClaimNotes(arNotes.id, editFormData);
                message = UPDATE_SUCCESS;
            }

            // Note: Removed 'dispatch(ListArClaimNotes)' here since it is handled in the component's .unwrap().then()
            return { result, message };

        } catch (error) {
            // Reject with value to handle error in the component
            return rejectWithValue(error?.response?.data?.message || i18n.t("errorMessages.commonError") || "Failed to save AR notes");
        }
    }
);

export const arFollowupNotes = createAsyncThunk(
    'claimbeta/arFollowupNotes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await service.GetARNoteTemplates();
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data
                    ? error.response.data
                    : error.message
            );
        }
    }
)

/**
 * API Call functionality to fetch ar-claim header data 
 */
export const ListArClaimNotes = createAsyncThunk(
    "claimbeta/ListArClaimNotes",
    async (payload, { rejectWithValue, getState }) => {
        let { claimbeta } = getState();
        let { openTabPks } = claimbeta
        try {
            const response = await service.ListArClaimNotes(payload || openTabPks[0]);
            return response.data.results
        } catch (error) {
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.error_fetching_ar_header_data") })
        }
    }
);

/**
 * API Call functionality to fetch ar-claim note details
 */
export const onEditArClaimNotes = createAsyncThunk(
    "claimbeta/onEditArClaimNotes",
    async (payload, thunkAPI) => {
        try {
            let response = await service.GetArClaimNotes(payload);
            return response.data
        } catch (error) {
            return thunkAPI.rejectWithValue({ message: i18n.t("claim_beta.api_errors.error_fetching_ar_notes") })
        }
    }
);

/**
 * API Call functionality to fetch ar-claim note details
 */
export const onDeleteArClaimNotes = createAsyncThunk(
    "claimbeta/onDeleteArClaimNotes",
    async (payload, { dispatch, rejectWithValue, getState }) => {
        let { claimbeta } = getState();
        let { claimPK } = claimbeta
        try {
            let response = await service.DeleteClaimNotes(payload);
            if (response.status == 204) {
                dispatch(ListArClaimNotes(claimPK))
            }
            return response.data
        } catch (error) {
            // Check if the error response exists and has a 403 status
            if (error.response && error.response.status === 403) {
                // You can still trigger side-effects here if necessary
                
                return rejectWithValue({ 
                    status: 403,
                    message: i18n.t("errorMessages.permission_error") 
                });
            }
            return rejectWithValue({ message: i18n.t("claim_beta.api_errors.error_deleting_ar_notes") })
        }
    }
);

/**
 * Function getArClaimFilterDataSet fetches all the drop-down-data required for the claim filter in one shot using promise chaining 
 */
export const getArClaimFilterDataSet = createAsyncThunk(
    "claimbeta/getArClaimFilterDataSet",
    async (payload, thunkAPI) => {
        const practicePK = getStorage('practice');

        const promises = [
            claim_service.ARStatusNoteList(0, 0, practicePK).then(response => {
                let data = []
                response.data.map((item) => {
                    data.push({
                        id: item.id,
                        name: item.name
                    })
                })
                return data
            }),
            claim_service.ARActionNoteList(0, 0, practicePK).then(response => {
                let data = []
                response.data.map((item) => {
                    data.push({
                        id: item.id,
                        value: item.id, 
                        name: item.name,
                        code: item.code || item.name,
                        follow_up_days_count: item.follow_up_days_count || item.follow_up_days || item.days || 30 // Critical property fix
                    })
                })
                return data
            })
        ];
        try {
            const [
                arStatusList,
                arActionCodeList,

            ] = await Promise.all(promises);

            return {
                arStatusList,
                arActionCodeList,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue({ errorMessage: i18n.t("errorMessages.error_drowdown_list_data") })
        }
    }
);


export const getFieldList = createAsyncThunk(
    "claimbeta/getFieldList",
    async (_, { rejectWithValue }) => {
        try {
            const response = await service.GetFieldGroup();
            return response.data

        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to list fields.")
        }
    }
)


export const fetchWorkflowDetails = createAsyncThunk(
    'claimbeta/fetchDetails',
    async ({id,method}, { rejectWithValue }) => {
        try {
            const practice_id = getStorage('practice')
            const response = await service.GetWorkflowDetails(id, practice_id);
            // Return the data if it exists
            return {data:response?.data,method:method};
        } catch (error) {
            console.error("Error fetching workflow details:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


export const fetchAuditorIssues = createAsyncThunk(
    'audit/fetchIssues',
    async ({ activeClaimStageId }, { rejectWithValue, getState }) => {
        try {
            const practiceId = getStorage('practice');
            let { claimbeta } = getState();
            let { claimPK, professionalClaimDetails} = claimbeta;

            if (!activeClaimStageId) return rejectWithValue(i18n.t("audit_review_popup.api_errors.claim_stage_id_missing"));

            const targetClaimId = professionalClaimDetails?.id || claimPK;

            // Passing activeClaimStageId to your service
            const response = await service.ListAuditorComments(targetClaimId, practiceId, activeClaimStageId);

            let rawList = [];
            if (response?.data && response?.data?.comments && Array.isArray(response.data.comments)) {
                rawList = response?.data?.comments;
            }

            // Return the list and the activeClaimStageId so the reducer can cache it accurately
            return { rawList, activeClaimStageId };
        } catch (error) {
            const backendMsg = error.response?.data?.message || "";
            const errorNotification = backendMsg === "invalid_user"
                ? i18n.t("claim_beta.api_errors.invalid_user")
                : i18n.t("claim_beta.api_errors.error_comments");

            return rejectWithValue(errorNotification);
        }
    }
);

const getErrorMessageKey = (backendMsg, backendError = "") => {
    if (backendError.includes("CAN_NOT_APPROVE_CLAIM_STAGE_NOT_COMPLETED")) {
        return "audit_review_popup.api_errors.can_not_update_status_to_complete_claim_status_either_in_todo_or_completed";
    }
    const errorMapping = {
        invalid_user: "invalid_user",
        invalid_claim_stage: "invalid_claim_stage",
        support_reason_not_found: "support_reason_not_found",
        field_group_not_found: "field_group_not_found",
        assigned_to_user_is_not_found: "assigned_to_user_is_not_found",
        auditor_comment_not_found: "auditor_comment_not_found",
        claim_stage_id_required: "claim_stage_id_required",
        add_auditor_comments_before_reprove:"add_auditor_comments_before_reprove" // Add if you create it in JSON
    };

    const key = errorMapping[backendMsg] || "something_went_wrong";
    return `audit_review_popup.api_errors.${key}`;
};

export const submitAuditorIssue = createAsyncThunk(
    'audit/submitIssue',
    async (payloadData, { rejectWithValue, getState,dispatch }) => {
        const { activeTab } = payloadData;
        
        // 1. Extract only what's necessary from state
        const { claimbeta } = getState();
        const { claimPK, professionalClaimDetails, workflowDetails, editingIssueId, auditReviewDetails } = claimbeta;

        const currentList = (Array.isArray(workflowDetails) ? workflowDetails : []);

        const selectedWorkflowForAudit = currentList.find(item => item?.workflow_id === activeTab) || null;

        // 2. Early Failures / Validations
        const selectedWf = selectedWorkflowForAudit;
        if (!selectedWorkflowForAudit) {
            return rejectWithValue({ isValidation: true, message: i18n.t("audit_review_popup.api_errors.invalid_selection")});
        }

        const targetClaimStageId = selectedWf?.claim_stage_id || workflowDetails.find(w => w.claim_stage_id)?.claim_stage_id;
        if (!targetClaimStageId) {
            return rejectWithValue({ isValidation: true, message:i18n.t("audit_review_popup.api_errors.claim_stage_missing")  });
        }

        if(!auditReviewDetails.comments || auditReviewDetails?.exceptionType?.length==0 || auditReviewDetails?.field?.length==0){
            return rejectWithValue({ isValidation: true, message: i18n.t("audit_review_popup.api_errors.required_field_missing")});
        }

        try {
            const targetWorkflowId = selectedWf?.workflow_id;
            const targetClaimId = professionalClaimDetails?.id || claimPK;
            const parsedReasons = auditReviewDetails?.exceptionType?.map(item => parseInt(item, 10)).filter(num => !isNaN(num)) || [];

            let response;

            // 3. Dispatch the appropriate API payload
            if (editingIssueId) {
                const updatePayload = {
                    description: auditReviewDetails.comments,
                    reasons: parsedReasons,
                    field_group_id: auditReviewDetails?.field,
                };
                response = await service.UpdateAuditorComment(editingIssueId, updatePayload);
            } else {
                const finalDescription = !selectedWf?.claim_stage_id 
                    ? `${auditReviewDetails.comments} [WF_TRACK:${targetWorkflowId}]` 
                    : auditReviewDetails.comments;

                const createPayload = {
                    claim_stage_id: parseInt(targetClaimStageId, 10),
                    description: finalDescription,
                    assigned_to: selectedWf?.user_id ? parseInt(selectedWf.user_id, 10) : null,
                    status_comment: "OPEN",
                    associate_user: parseInt(getStorage('userId') || selectedWf?.user_id, 10) || null,
                    reasons: parsedReasons,
                    field_group_id: auditReviewDetails?.field,
                    workflow_id: parseInt(targetWorkflowId, 10),
                    claim_id: parseInt(targetClaimId, 10),
                    current_practice_id: parseInt(getStorage('practice'), 10) || 6
                };
                response = await service.AddAuditorComments(createPayload);
            }

            // 4. Handle Response States
            if (response.status === 200 || response.status === 201) {
                dispatch(fetchAuditorIssues({activeClaimStageId:targetClaimStageId}))
                return { editingIssueId, targetClaimStageId, data: response.data };
            }

            // Map backend error string to translation
            const translationKey = getErrorMessageKey(response?.data?.message);
            return rejectWithValue({ isValidation: true, message: i18n.t(translationKey) });

        } catch (error) {
            console.error("Action failed:", error);
            return rejectWithValue({ isValidation: true, message: i18n.t("claim_beta.api_errors.something_went_wrong") });
        }
    }
);


export const DeleteAuditorIssue = createAsyncThunk(
    'audit/deleteIssue',
    async (issue, { rejectWithValue,dispatch }) => {
        try {
            const response = await service.DeleteAuditorComment(issue?.id);
            // Check for successful status codes
            if (response.status === 200 || response.status === 204) {
                dispatch(fetchAuditorIssues({activeClaimStageId:issue?.claim_stage_id}))
                return issue?.id; // Return the id so the reducer knows what was deleted
            }
            return rejectWithValue(i18n.t("audit_review_popup.api_errors.delete_failed"));
        } catch (error) {
            return rejectWithValue(error.response?.data || i18n.t("audit_review_popup.api_errors.delete_failed"));
        }
    }
);

export const reproveStage = createAsyncThunk(
    'audit/reproveStage',
    async ({ activeTab }, { rejectWithValue, getState,dispatch }) => {
        try {

            const { claimbeta } = getState();
            const { workflowDetails, professionalClaimDetails, claimPK } = claimbeta;

            const currentList = (Array.isArray(workflowDetails) ? workflowDetails : []);

            const selectedWorkflowForAudit = currentList.find(item => item?.workflow_id === activeTab) || null;

            const selectedWf = selectedWorkflowForAudit;

            const practiceId = getStorage('practice');

            if (!activeTab) {
                return rejectWithValue({ isValidation: true, message: i18n.t("audit_review_popup.api_errors.invalid_selection") })
            }

            if (!selectedWf?.claim_stage_id) {
                return rejectWithValue({ isValidation: true, message: `Cannot reprove "${selectedWf?.workflow_name || 'this stage'}" because it has not been initialized yet.` });
            }



            const payload = {
                claim_stage_id: parseInt(selectedWf.claim_stage_id, 10),
                action: "REPROVE",
                assigned_to_id: selectedWf?.user_id ? parseInt(selectedWf.user_id, 10) : null,
                practice_id: practiceId ? parseInt(practiceId, 10) : 6
            };

            // 1. Fire the main Reprove Action
            const response = await service.ApproveOrReproveClaimStage(payload);

            if (response.status === 200 || response.status === 201) {
                let updatedWorkflowData = null;
                const targetClaimId = professionalClaimDetails?.id || claimPK;
                if (targetClaimId && practiceId) {
                    dispatch(fetchWorkflowDetails({id:targetClaimId,method:"put"}));
                }
                return {
                    success: true,
                    updatedWorkflowData
                };
            }

            const backendMsg = response?.data?.message || "";
            const backendError = response?.data?.error_message || "";

            const translationKey = getErrorMessageKey(backendMsg, backendError);
            return rejectWithValue({ isValidation: true, message: i18n.t(translationKey) });

        } catch (error) {
            console.error("Action failed:", error);
            return rejectWithValue({ isValidation: true, message: i18n.t("claim_beta.api_errors.something_went_wrong") });
        }
    }
);

export const approveStage = createAsyncThunk(
    'audit/approveStage',
    async ({ activeTab }, { rejectWithValue, getState,dispatch }) => {
        try {

            const { claimbeta } = getState();
            const { workflowDetails, professionalClaimDetails, claimPK } = claimbeta;

            const currentList = (Array.isArray(workflowDetails) ? workflowDetails : []);

            const selectedWorkflowForAudit = currentList.find(item => item?.workflow_id === activeTab) || null;

            const selectedWf = selectedWorkflowForAudit;

            const practiceId = getStorage('practice');

            if (!activeTab) {
                return rejectWithValue({ isValidation: true, message: i18n.t("audit_review_popup.api_errors.invalid_selection") })
            }

            if (!selectedWf?.claim_stage_id) {
                return rejectWithValue({ isValidation: true, message: `Cannot reprove "${selectedWf?.workflow_name || 'this stage'}" because it has not been initialized yet.` });
            }



            const payload = {
                claim_stage_id: parseInt(selectedWf.claim_stage_id, 10),
                action: "APPROVE",
                assigned_to_id: selectedWf?.user_id ? parseInt(selectedWf.user_id, 10) : null,
                practice_id: practiceId ? parseInt(practiceId, 10) : 6
            };

            // 1. Fire the main Reprove Action
            const response = await service.ApproveOrReproveClaimStage(payload);

            if (response.status === 200 || response.status === 201) {
                let updatedWorkflowData = null;
                const targetClaimId = professionalClaimDetails?.id || claimPK;
                if (targetClaimId && practiceId) {
                    dispatch(fetchWorkflowDetails({id:targetClaimId,method:"put"}));
                }
                return {
                    success: true,
                    updatedWorkflowData
                };
            }

            const backendMsg = response?.data?.message || "";
            const backendError = response?.data?.error_message || "";

            const translationKey = getErrorMessageKey(backendMsg, backendError);
            return rejectWithValue({ isValidation: true, message: i18n.t(translationKey) });

        } catch (error) {
            console.error("Action failed:", error);
            return rejectWithValue({ isValidation: true, message: i18n.t("claim_beta.api_errors.something_went_wrong") });
        }
    }
);

export const submitEscalationResponse = createAsyncThunk(
    'claimBeta/submitEscalationResponse',
    async (payload, { rejectWithValue }) => {
        const { claimId, escalationId, selectedFiles = [], deEscalationDetails } = payload;

        // VALIDATIONS
        if (!claimId || !escalationId) {
            return rejectWithValue({ type: 'error', message: i18n.t('review_respond_popup.missing_id') });
        }

        if (!(deEscalationDetails?.remarks || selectedFiles?.length > 0)) {
            return rejectWithValue({
                type: 'error',
                message: i18n.t('review_respond_popup.empty_submission')
            });
        }

        if (selectedFiles.length > 10) {
            return rejectWithValue({ type: 'error', message: i18n.t('review_respond_popup.max_file_upload') });
        }

        const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'csv', 'txt'];
        const maxSizeInBytes = 50 * 1024 * 1024; // 50MB
        const errorList = [];

        selectedFiles.forEach((file) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                errorList.push(`"${file.name}" (Invalid format)`);
            } else if (file.size > maxSizeInBytes) {
                errorList.push(`"${file.name}" (Exceeds 50MB)`);
            }
        });

        if (errorList.length > 0) {
            return rejectWithValue({ type: 'error', message: i18n.t('review_respond_popup.invalid_files_found', { files: errorList.join(', ') })});
        }

        // API EXECUTION 
        try {
            const formData = new FormData();
            
            formData.append('manager_support_request_id', escalationId);
            formData.append('response', deEscalationDetails?.remarks || "");

            // If files exist, append them. If not, the backend just gets an empty documents array.
            if (selectedFiles && selectedFiles.length > 0) {
                selectedFiles.forEach((file) => {
                    formData.append('documents', file);
                });
            }

            // Call API
            const finalResponse = await claim_service.DeEscalateClaim(formData);

            // HANDLE RESPONSE
            const submitCheck = handleApiErrors(finalResponse.status, finalResponse.data);

            if (submitCheck.hasError) {
                return rejectWithValue({ type: 'error', message: submitCheck.msg });
            }

            return finalResponse.data;

        } catch (error) {
            let errorMessage = error.message || i18n.t('review_respond_popup.failed_submit');
            const { status, data } = error.response || {};

            if (status || data) {
                const catchCheck = handleApiErrors(status, data);
                if (catchCheck.hasError) {
                    errorMessage = catchCheck.msg;
                }
            }
            return rejectWithValue({ type: 'error', message: errorMessage });
        }
    }
);

export const saveClaimOnHold = createAsyncThunk(
    'claimbeta/saveClaimOnHold',
    async ({hideReleaseHold=false}, { getState, rejectWithValue,dispatch }) => {
        try {
            const state = getState().claimbeta;
            const practicePK = getStorage("practice");
            let claimIDs = [];

            if (!hideReleaseHold) {
                const selectedItems = state.selection?.claimIDs;

                if (selectedItems?.length > 0) {
                    claimIDs = selectedItems.map(item =>  item.id);
                } else if (state.claimPK) {
                    // Force single claimPK into an array
                    claimIDs = [state.claimPK];
                }

            } else {
                const singleClaimId = state.professionalClaimDetails?.id || state.claimPK;
                if (singleClaimId) {
                    claimIDs = [singleClaimId];
                }
            }
            if(claimIDs?.length == 0){
                dispatch(showNotifications({ isopen: true, message: i18n.t("claim_beta.api_errors.claim_id_missing"), status: "error" }))
                return;
            }
            

            // Renamed from 'payload' to 'apiPayload' to prevent shadowing
            const apiPayload = {
                claim_hold_status_pk: Number(state.holdModal?.status),
                practice_pk: Number(practicePK),
                selected_claim_ids: claimIDs,
                hold_comment: state.holdModal?.reason || "",
            };

            const response = await claim_service.SaveOnHold(apiPayload);

            if(response.status==400){
                const errorMessage = response.data?.error_message || response.data?.message;
                return rejectWithValue({message:errorMessage ?? i18n.t('review_respond_popup.failed_submit')})
            }

            return response.data;
        } catch (error) {
            // 1. Determine fallback localized message
            const fallbackMessage = i18n.t('review_respond_popup.failed_submit');

            // 2. Reject with backend response object/message or fallback string
            return rejectWithValue(error?.response?.data || error?.message || fallbackMessage);
        }
    }
);

export const releaseClaimHold = createAsyncThunk(
  'claimbeta/releaseClaimHold',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState().claimbeta;
      const practicePK = getStorage('practice');

      // 1. Extract raw items from Redux state
      let selectedItems = state.selection?.claimIDs || [];

      // Single Claim Detail View Fallback

      if (params) {
        const singleId = state.professionalClaimDetails?.id;
        if (singleId) {
          selectedItems = [singleId];
        }
      }

      // 2. Normalize into clean numeric IDs
      const IDS = Array.isArray(selectedItems) ? selectedItems : [selectedItems];
      const selectedIds = IDS
        .map(item => Number(item?.id ?? item?.claim_pk ?? item))
        .filter(id => id > 0);

      // Guard: No valid claim selected
      if (selectedIds.length === 0) {
        return rejectWithValue({
          message: i18n.t('claim_beta.api_errors.no_claim_selected'),
        });
      }

      // 3. Build API Payload directly from state
      const payload = {
        selected_claim_ids: selectedIds,
        practice_pk: Number(practicePK),
        unhold_comment: state.releaseModal?.reason || '',
      };

      // ONLY attach status PK if releasing from the Single Claim Detail View page
      const releaseStatusPk = state.holdModal?.status;
      if (releaseStatusPk && params) {
        payload.claim_status_id = Number(releaseStatusPk);
      }

      // 4. API Service Call
      const response = await claim_service.ReleaseOnHold(payload);

      // Handle HTTP 400 Bad Request
      if ([response?.status, response?.status_code].includes(400)) {
        // Safely convert objects/arrays to string format if needed
        const rawError = response?.data?.error_message || response?.data?.message || response?.data;

          const errorMessage = typeof rawError === 'object' && rawError !== null
              ? JSON.stringify(rawError)
              : String(rawError || '');

        const knownValidationKeys = ['selected_claim_status_is_not_on_hold', 'not_found'];

          // Check if any of the targeted keys are present in the error message
          const isValidationError = knownValidationKeys.some((key) =>
              errorMessage.includes(key)
          );

          const displayMessage = isValidationError
              ? i18n.t('claim_beta.api_errors.release_claim_validation')
              : (errorMessage || i18n.t('review_respond_popup.failed_submit'));

          return rejectWithValue({ message: displayMessage });
      }

      return response.data || response;
    } catch (error) {
      const errorPayload = error?.response?.data || error?.data || error;
      return rejectWithValue(
        errorPayload || {
          message: i18n.t('claim_beta.api_erros.failed_to_release_claim_hold'),
        }
      );
    }
  }
);

export const fetchClaimHoldStatuses = createAsyncThunk(
  'claimbeta/fetchClaimHoldStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const practicePK = getStorage('practice');
      const response = await claim_service.ListClaimStatus(100, 1, practicePK, 'HOLD'); 
      const results = response?.data?.results || [];

      // Find ONLY the "Claim on Hold" status item
      const holdItem = results.find(
        (item) => item.name?.trim().toLowerCase() === CLAIM_ON_HOLD
      );

      if (holdItem) {
        const singleOption = [{
          id: holdItem.id,
          name: holdItem.name,
          value: holdItem.id,
        }];

        return {
          options: singleOption,
          selectedStatus: holdItem.id,
        };
      }

      return { options: [], selectedStatus: '' };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);